import { Logging } from '@vcd-ecm-ui/Common';

function displayImageOnCanvas(
  canvas: HTMLCanvasElement,
  image: HTMLImageElement,
  initialDraw: boolean,
  zoomInElements: string[],
  zoomOutElements: string[],
  zoomPercentageElements: string[],
  restoreElements: string[],
): void {
  // Canvas Width / Height
  const widthCanvas: number = canvas.width;
  const heightCanvas: number = canvas.height;

  // View x / y Offset
  let xleftView = 0;
  let ytopView = 0;

  // Current View Width and Height
  let widthView: number = widthCanvas;
  let heightView: number = heightCanvas;

  // Flag indicating if Left Mouse button is down
  let mouseDown = false;

  // Last X & Y mouse coordinates
  let lastX = 0;
  let lastY = 0;

  // Scale
  let canvasScale = 1.0;

  Logging.Debug('Display Image on Canvas', widthCanvas, heightCanvas);

  // Get the 2D Context for the Canvas
  const context: CanvasRenderingContext2D = canvas.getContext('2d') as CanvasRenderingContext2D;

  function updateZoomPercentage(): void {
    const scale: number = (1.0 + (1.0 - canvasScale)) * 100;
    const scaleText = `${scale.toFixed(0)} %`;

    zoomPercentageElements.forEach((elementId: string) => {
      const element: HTMLParagraphElement = document.getElementById(elementId) as HTMLParagraphElement;

      if (element) {
        element.innerText = scaleText;
      }
    });
  }

  function draw(): void {
    // Reset the Context
    context.setTransform(1, 0, 0, 1, 0, 0);

    // Clears the Canvas
    context.clearRect(0, 0, canvas.width, canvas.height);

    const scale = (1 - canvasScale) + 1;

    // Scale the Context
    context.scale(widthCanvas / widthView, heightCanvas / heightView);

    // Translate the Context
    context.translate(-xleftView, -ytopView);

    Logging.Trace(
      'Draw',
      scale.toFixed(2),
      xleftView.toFixed(0),
      ytopView.toFixed(0),
      widthView.toFixed(0),
      heightView.toFixed(0),
    );

    // Draw the Image on the Context
    context.drawImage(image, 0, 0);

    updateZoomPercentage();
  }

  function limitOffset(): void {
    // Prevent Panning beyond the Left of the Image
    if (xleftView < 0) {
      xleftView = 0;
    }

    // Prevent Panning beyond the Right of the Image
    if ((xleftView + widthView) > widthCanvas) {
      xleftView = widthCanvas - widthView;
    }

    // Prevent Panning beyond the Top of the Image
    if (ytopView < 0) {
      ytopView = 0;
    }

    // Prevent Panning beyond the Bottom of the Image
    if ((ytopView + heightView) > heightCanvas) {
      ytopView = heightCanvas - heightView;
    }
  }

  function handleDblClick(event: MouseEvent): void {
    // Canvas coordinates
    const X = event.clientX - canvas.offsetLeft - canvas.clientLeft + canvas.scrollLeft;
    const Y = event.clientY - canvas.offsetTop - canvas.clientTop + canvas.scrollTop;

    // View coordinates
    let x = (X / widthCanvas) * widthView + xleftView;
    let y = (Y / heightCanvas) * heightView + ytopView;

    // Calculate the Scale, Shrink (1.5) if shift key pressed
    const scale = event.shiftKey === true ? 0.1 : -0.1;

    // Update the Canvas Scale
    canvasScale += scale;

    // Limit the Canvas Scale
    if (canvasScale > 1.0) {
      canvasScale = 1.0;
    }

    // Adjust the Width & Height using the Scale
    widthView = widthCanvas * canvasScale;
    heightView = heightCanvas * canvasScale;

    // If the Width or Height is larger than the Canvas Width or Height, don't zoom in any further.
    if (widthView > widthCanvas || heightView > heightCanvas) {
      // Use the Canvas View Width & Height
      widthView = widthCanvas;
      heightView = heightCanvas;

      // Get the Center of the View
      x = widthView / 2;
      y = heightView / 2;

      canvasScale = 1.0;
    }

    xleftView = x - widthView / 2;
    ytopView = y - heightView / 2;

    limitOffset();

    draw();
  }

  function handleMouseDown(event: MouseEvent): void {
    // Left Mouse Button is Down
    mouseDown = true;
    Logging.Trace('Mouse Down');
  }

  function handleMouseUp(event: MouseEvent): void {
    // Left Mouse Button is Up
    mouseDown = false;
    Logging.Trace('Mouse Up');
  }

  function handleMouseMove(event: MouseEvent): void {
    // Canvas coordinates
    const X = event.clientX - canvas.offsetLeft - canvas.clientLeft + canvas.scrollLeft;
    const Y = event.clientY - canvas.offsetTop - canvas.clientTop + canvas.scrollTop;

    if (mouseDown) {
      // View coordinates
      const dx = ((X - lastX) / widthCanvas) * widthView;
      const dy = ((Y - lastY) / heightCanvas) * heightView;

      xleftView -= dx;
      ytopView -= dy;

      limitOffset();
    }

    // Store the Last X & Y coordinates
    lastX = X;
    lastY = Y;

    if (mouseDown) {
      draw();
    }
  }

  function reset(): void {
    xleftView = 0;
    ytopView = 0;

    widthView = widthCanvas;
    heightView = heightCanvas;

    canvasScale = 1.0;

    draw();
  }

  function zoomAtCenter(scale: number): void {
    // Get the Center of View
    let x = widthView / 2 + xleftView;
    let y = heightView / 2 + ytopView;

    // Update the Canvas Scale
    canvasScale += scale;

    // Limit the Canvas Scale
    if (canvasScale > 1.0) {
      canvasScale = 1.0;
    }

    // Adjust the Width & Height using the Scale
    widthView = widthCanvas * canvasScale;
    heightView = heightCanvas * canvasScale;

    // If the Width or Height is larger than the Canvas Width or Height, don't zoom in any further.
    if (widthView > widthCanvas || heightView > heightCanvas) {
      // Use the Canvas View Width & Height
      widthView = widthCanvas;
      heightView = heightCanvas;

      // Get the Center of the View
      x = widthView / 2;
      y = heightView / 2;

      canvasScale = 1.0;
    }

    // Scale about center of view, rather than mouse position. This is different than dblclick behavior.
    xleftView = x - widthView / 2;
    ytopView = y - heightView / 2;

    limitOffset();

    draw();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleMouseWheel(event: any): void {
    // Calculate the Scale
    const scale = (event.wheelDelta < 0 || event.detail > 0) ? 0.1 : -0.1;

    zoomAtCenter(scale);
  }

  // Double click to zoom in at point, shift double click to zoom out.
  canvas.addEventListener('dblclick', handleDblClick, false);

  // Click and hold to pan
  canvas.addEventListener('mousedown', handleMouseDown, false);
  canvas.addEventListener('mousemove', handleMouseMove, false);
  canvas.addEventListener('mouseup', handleMouseUp, false);

  // Zoom in / Out at Center
  // canvas.addEventListener('mousewheel', handleMouseWheel, false);
  // canvas.addEventListener('DOMMouseScroll', handleMouseWheel, false); // For Firefox

  // External Zoom In Controls
  zoomInElements.forEach((elementId: string) => {
    const element: HTMLButtonElement = document.getElementById(elementId) as HTMLButtonElement;

    if (element) {
      element.addEventListener('click', (event: MouseEvent) => {
        zoomAtCenter(-0.1);
      });
    }
  });

  // External Zoom Out Controls
  zoomOutElements.forEach((elementId: string) => {
    const element: HTMLButtonElement = document.getElementById(elementId) as HTMLButtonElement;

    if (element) {
      element.addEventListener('click', (event: MouseEvent) => {
        zoomAtCenter(0.1);
      });
    }
  });

  // External Restore Controls
  restoreElements.forEach((elementId: string) => {
    const element: HTMLButtonElement = document.getElementById(elementId) as HTMLButtonElement;

    if (element) {
      element.addEventListener('click', (event: MouseEvent) => {
        reset();
      });
    }
  });

  if (initialDraw === true) {
    draw();
  }
}

export default displayImageOnCanvas;
